import * as React from "react"
import { createPortal } from "react-dom"
import { cn } from "../lib/utils"
import { useDomainEvent } from "@soap/modules";

interface DropdownMenuProps {
  children: React.ReactNode
  fullWidth?: boolean
}

interface DropdownMenuTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
}

interface DropdownMenuContentProps extends React.HTMLAttributes<HTMLDivElement> {
  sideOffset?: number
  side?: 'top' | 'bottom'
  align?: 'start' | 'center' | 'end'
}

interface DropdownMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  inset?: boolean
}

const DropdownMenuContext = React.createContext<{
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  containerRef: React.RefObject<HTMLDivElement>
  fullWidth: boolean
}>({
  isOpen: false,
  setIsOpen: () => {},
  containerRef: { current: null },
  fullWidth: false
})

const DropdownMenu = ({ children, fullWidth = false }: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  return (
    <DropdownMenuContext.Provider value={{ isOpen, setIsOpen, containerRef, fullWidth }}>
      <div className={cn("relative text-left", fullWidth ? "block w-full" : "inline-block")} ref={containerRef}>
        {children}
      </div>
    </DropdownMenuContext.Provider>
  )
}

const DropdownMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  DropdownMenuTriggerProps
>(({ className, children, asChild, ...props }, ref) => {
  const { isOpen, setIsOpen } = React.useContext(DropdownMenuContext)
  
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement, {
      ref,
      className: cn(className, (children as React.ReactElement).props.className),
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        setIsOpen(!isOpen)
        if ((children as React.ReactElement).props.onClick) {
          (children as React.ReactElement).props.onClick(e)
        }
      },
      ...props
    })
  }
  
  return (
    <button
      ref={ref}
      className={cn(className)}
      onClick={() => setIsOpen(!isOpen)}
      {...props}
    >
      {children}
    </button>
  )
})
DropdownMenuTrigger.displayName = "DropdownMenuTrigger"

const DropdownMenuContent = React.forwardRef<
  HTMLDivElement,
  DropdownMenuContentProps
>(({ className, sideOffset = 4, side, align, ...props }, ref) => {
  const { isOpen, setIsOpen, containerRef, fullWidth } = React.useContext(DropdownMenuContext)
  const [position, setPosition] = React.useState({ top: 0, left: 0, width: 0 })
  const [cssVars, setCssVars] = React.useState<Record<string, string>>({})
  const contentRef = React.useRef<HTMLDivElement>(null)

  useDomainEvent({
    eventName: "close-context-menu",
    onEventReceived: (event) => {
      //* this occurs when we return from editing
      setIsOpen(false);
    }
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node) &&
          contentRef.current && !contentRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, setIsOpen, containerRef])

  // CSS variables are now on :root, so no need to extract from .lyw-container

  // Calculate position for portal rendering
  React.useEffect(() => {
    if (!isOpen || !containerRef.current) return

    const updatePosition = () => {
      const container = containerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      const contentRect = contentRef.current?.getBoundingClientRect()
      const contentHeight = contentRect?.height || 200 // Estimate if not available
      const viewportHeight = window.innerHeight

      let top: number
      let left = rect.right

      // Respect the side prop - if "top", always show above; if "bottom" or undefined, show below (with auto-flip if needed)
      if (side === 'top') {
        // Always position above
        top = rect.top - contentHeight - sideOffset
      } else {
        // Default to below, but flip if no space
        top = rect.bottom + sideOffset
        // Check if there's space below, if not show above
        if (top + contentHeight > viewportHeight && rect.top - contentHeight > 0) {
          top = rect.top - contentHeight - sideOffset
        }
      }

      // Align end by default
      if (align === 'end') {
        left = rect.right - (contentRect?.width || 150)
      } else if (align === 'start') {
        left = rect.left
      }

      setPosition({ top, left, width: rect.width })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen, sideOffset, side, align])

  // Combine refs - CSS variables are now on :root, so no manual application needed
  const setRefs = React.useCallback((node: HTMLDivElement | null) => {
    if (contentRef.current !== node) {
      (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node
      if (ref && typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node
      }
    }
  }, [ref])

  if (!isOpen) return null

  const content = (
    <div
      ref={setRefs}
      className={cn(
        "fixed z-[9999] min-w-[8rem] overflow-hidden rounded-md border bg-white p-1 text-gray-950 shadow-md animate-in fade-in-0 zoom-in-95 flex flex-col",
        className
      )}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        ...(fullWidth && position.width > 0 ? { width: `${position.width}px` } : {}),
      }}
      {...props}
    />
  )

  return createPortal(content, document.body)
})
DropdownMenuContent.displayName = "DropdownMenuContent"

const DropdownMenuItem = React.forwardRef<
  HTMLDivElement,
  DropdownMenuItemProps
>(({ className, inset, children, onClick, ...props }, ref) => {
  const { setIsOpen } = React.useContext(DropdownMenuContext)
  
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    
    // Check if this is a dialog menu item (has data-dialog-item attribute)
    const isDialogItem = (e.currentTarget as HTMLElement).hasAttribute('data-dialog-item')
    
    // Call the onClick handler first
    if (onClick) {
      onClick(e)
    }
    
    // For dialog items, don't close the menu - the dialog will close it via "close-context-menu" event
    // For simple actions (like selecting a provider), close the menu immediately
    if (!isDialogItem) {
      // Use requestAnimationFrame to ensure React has processed the state update
      requestAnimationFrame(() => {
        setIsOpen(false)
      })
    }
  }
  
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Stop propagation to prevent click-outside handler from closing menu
    // before onClick can execute
    e.stopPropagation()
  }
  
  return (
    <div
      ref={ref}
      className={cn(
        "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm font-normal outline-none transition-colors hover:bg-gray-100 focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        inset && "pl-8",
        className
      )}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      {...props}
    >
      {children}
    </div>
  )
})
DropdownMenuItem.displayName = "DropdownMenuItem"

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
}
